import React from "react";
import { FeatherCheckCircle, FeatherXCircle, FeatherPlus, FeatherAlertCircle } from "@subframe/core";
import { ExcelColumn } from "../types";
import { ColumnComparisonResult, ColumnMatch } from "../utils/columnMapping";

interface ColumnComparisonPanelProps {
  comparison: ColumnComparisonResult;
  selectedNewColumns: Set<string>;
  onToggleNewColumn: (columnId: string) => void;
  onSelectAllNewColumns: () => void;
  onDeselectAllNewColumns: () => void;
}

export const ColumnComparisonPanel: React.FC<ColumnComparisonPanelProps> = ({
  comparison,
  selectedNewColumns,
  onToggleNewColumn,
  onSelectAllNewColumns,
  onDeselectAllNewColumns,
}) => {
  const { matchingColumns, missingColumns, newColumns, isValid, warnings } = comparison;

  return (
    <div className="flex flex-col gap-4 p-4 border border-neutral-border rounded-lg bg-default-background">
      <h3 className="text-heading-3 font-heading-3 text-default-font">
        Column Analysis
      </h3>

      {/* Matching Columns */}
      {matchingColumns.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FeatherCheckCircle className="w-5 h-5 text-success-600" />
            <span className="text-body-bold font-body-bold text-success-700">
              Matching Columns ({matchingColumns.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-2 ml-7">
            {matchingColumns.map((match) => (
              <span
                key={match.savedColumnId}
                className="px-3 py-1 text-sm bg-success-50 text-success-700 rounded-md border border-success-200"
              >
                {match.savedColumnName}
                {match.matchType === "case-insensitive" && (
                  <span className="text-xs ml-1 opacity-70">(case match)</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Missing Columns - Error */}
      {missingColumns.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FeatherXCircle className="w-5 h-5 text-error-600" />
            <span className="text-body-bold font-body-bold text-error-700">
              Missing Columns ({missingColumns.length}) - Required
            </span>
          </div>
          <div className="flex flex-wrap gap-2 ml-7">
            {missingColumns.map((col) => (
              <span
                key={col}
                className="px-3 py-1 text-sm bg-error-50 text-error-700 rounded-md border border-error-200"
              >
                {col}
              </span>
            ))}
          </div>
          <div className="ml-7 p-3 bg-error-50 border border-error-200 rounded-md">
            <p className="text-body font-body text-error-700">
              <FeatherXCircle className="inline w-4 h-4 mr-1" />
              Cannot proceed: The uploaded file is missing required columns. Please upload a file with all required columns.
            </p>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <FeatherAlertCircle className="w-5 h-5 text-warning-600" />
            <span className="text-body-bold font-body-bold text-warning-700">
              Warnings
            </span>
          </div>
          <div className="ml-7 p-3 bg-warning-50 border border-warning-200 rounded-md">
            {warnings.map((warning, idx) => (
              <p key={idx} className="text-body font-body text-warning-700">
                {warning}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* New Columns - Selectable */}
      {newColumns.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FeatherPlus className="w-5 h-5 text-brand-600" />
              <span className="text-body-bold font-body-bold text-brand-700">
                New Columns Detected ({newColumns.length})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onSelectAllNewColumns}
                className="text-sm text-brand-600 hover:text-brand-800 underline"
              >
                Select All
              </button>
              <span className="text-subtext-color">|</span>
              <button
                type="button"
                onClick={onDeselectAllNewColumns}
                className="text-sm text-brand-600 hover:text-brand-800 underline"
              >
                Deselect All
              </button>
            </div>
          </div>
          <p className="ml-7 text-sm text-subtext-color">
            Select columns to create a new pivot view with these additional columns.
          </p>
          <div className="flex flex-wrap gap-2 ml-7">
            {newColumns.map((col) => {
              const isSelected = selectedNewColumns.has(col.id);
              return (
                <button
                  key={col.id}
                  type="button"
                  onClick={() => onToggleNewColumn(col.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-all cursor-pointer ${
                    isSelected
                      ? "bg-brand-100 text-brand-700 border-brand-300 ring-2 ring-brand-200"
                      : "bg-neutral-50 text-default-font border-neutral-200 hover:border-brand-300 hover:bg-brand-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="w-4 h-4 accent-brand-600"
                  />
                  <span>{col.name}</span>
                  <span className="text-xs text-subtext-color capitalize">
                    ({col.type})
                  </span>
                </button>
              );
            })}
          </div>
          {selectedNewColumns.size > 0 && (
            <div className="ml-7 mt-2 p-3 bg-brand-50 border border-brand-200 rounded-md">
              <p className="text-body font-body text-brand-700">
                <FeatherCheckCircle className="inline w-4 h-4 mr-1" />
                {selectedNewColumns.size} new column(s) selected. You can create a new pivot view with these columns after applying the existing configuration.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center gap-4 pt-4 border-t border-neutral-border">
        <div className="flex items-center gap-2">
          <span className="text-body font-body text-subtext-color">Status:</span>
          {isValid ? (
            <span className="flex items-center gap-1 text-success-600">
              <FeatherCheckCircle className="w-4 h-4" />
              Ready to apply
            </span>
          ) : (
            <span className="flex items-center gap-1 text-error-600">
              <FeatherXCircle className="w-4 h-4" />
              Missing required columns
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

